import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Type, Save, Home, Info, Phone, Star, Grid3X3, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { authService } from '@/utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Список страниц для редактирования
const PAGES = [
  { id: 'header', name: 'Хедер', nameLv: 'Header', icon: Phone },
  { id: 'home', name: 'Главная', nameLv: 'Sākumlapa', icon: Home },
  { id: 'about', name: 'О нас', nameLv: 'Par mums', icon: Info },
  { id: 'contacts', name: 'Контакты', nameLv: 'Kontakti', icon: Phone },
  { id: 'reviews', name: 'Отзывы', nameLv: 'Atsauksmes', icon: Star },
  { id: 'gallery', name: 'Галерея', nameLv: 'Galerija', icon: Grid3X3 },
  { id: 'order', name: 'Заказ', nameLv: 'Pasūtījums', icon: ShoppingBag }
];

// Структура контента по умолчанию для всех страниц
const defaultContent = {
  header: {
    contact_phone: '+371 28448991',
    contact_email: 'pure.atelier.riga@gmail.com'
  },
  home: {
    hero_title: { ru: 'Pure Atelier Riga', lv: 'Pure Atelier Riga' },
    hero_subtitle: { ru: 'Fine Jewelry & Engagement Rings', lv: 'Fine Jewelry & Engagement Rings' },
    hero_description: { 
      ru: 'Эксклюзивная коллекция ювелирных изделий для особенных моментов.',
      lv: 'Ekskluzīva juvelierizstrādājumu kolekcija īpašiem brīžiem.'
    },
    reviews_title: { ru: 'Отзывы клиентов', lv: 'Klientu atsauksmes' },
    reviews_description: {
      ru: 'Наши клиенты делятся своим опытом с украшениями Pure Atelier',
      lv: 'Mūsu klienti dalās savā pieredzē ar Pure Atelier juvelierizstrādājumiem'
    }
  },
  about: {
    page_title: { ru: 'О Pure Atelier', lv: 'Par Pure Atelier' },
    page_description: {
      ru: 'Молодая компания в Риге, специализирующаяся на кастомных ювелирных изделиях и помолвочных кольцах',
      lv: 'Jauns uzņēmums Rīgā, kas specializējas pielāgotos juvelierizstrādājumos un saderināšanās gredzenos'
    },
    story_title: { ru: 'Наша история', lv: 'Mūsu stāsts' },
    story_text1: {
      ru: 'Pure Atelier Riga — молодая и динамичная компания в мире ювелирных изделий Риги.',
      lv: 'Pure Atelier Riga ir jauns un dinamisks uzņēmums Rīgas juvelierizstrādājumu pasaulē.'
    },
    story_text2: {
      ru: 'Мы работаем под заказ - вы можете выбрать любую оправу, любой камень и любую чистоту.',
      lv: 'Mēs strādājam pēc pasūtījuma - jūs varat izvēlēties jebkuru ietvaru, jebkuru akmeni un jebkuru tīrību.'
    },
    story_text3: {
      ru: 'Наша цель — предлагать украшения высочайшего качества по лучшим ценам в Риге.',
      lv: 'Mūsu mērķis ir piedāvāt augstākās kvalitātes juvelierizstrādājumus par labākajām cenām Rīgā.'
    },
    feature1_title: { ru: 'Кастомные дизайны', lv: 'Pielāgoti dizaini' },
    feature1_desc: { ru: 'Создаём уникальные украшения по вашим пожеланиям', lv: 'Izveidojam unikālus juvelierizstrādājumus pēc jūsu vēlmēm' },
    feature2_title: { ru: 'Помолвочные кольца', lv: 'Saderināšanās gredzeni' },
    feature2_desc: { ru: 'Наша специальность - идеальные кольца для вашего важного момента', lv: 'Mūsu specialitāte - ideālie gredzeni jūsu svarīgākajam brīdim' },
    feature3_title: { ru: 'Лучшие цены в Риге', lv: 'Labākās cenas Rīgā' },
    feature3_desc: { ru: 'Предлагаем премиум качество по доступным ценам', lv: 'Piedāvājam premium kvalitāti par pieejamām cenām' },
    feature4_title: { ru: 'В наличии и под заказ', lv: 'Pieejami un pēc pasūtījuma' },
    feature4_desc: { ru: 'Выбирайте из каталога или закажите уникальное изделие', lv: 'Izvēlieties no kataloga vai pasūtiet unikālu izstrādājumu' }
  },
  contacts: {
    page_title: { ru: 'Контакты', lv: 'Kontakti' },
    page_description: {
      ru: 'Свяжитесь с нами, чтобы обсудить ваше будущее украшение',
      lv: 'Sazināties ar mums, lai apspriestu jūsu nākamo juvelierizstrādājumu'
    },
    phone: '+371 28448991',
    email: 'pure.atelier.riga@gmail.com',
    location: { ru: 'Рига, Латвия', lv: 'Rīga, Latvija' },
    working_hours: { ru: 'По предварительной записи', lv: 'Pēc iepriekšējas vienošanās' },
    form_title: { ru: 'Связаться с нами', lv: 'Sazināties' },
    form_description: {
      ru: 'Заполните контактную форму, и мы свяжемся с вами в течение 24 часов.',
      lv: 'Aizpildiet kontaktformu, un mēs sazināsimies ar jums 24 stundu laikā.'
    }
  },
  reviews: {
    page_title: { ru: 'Отзывы клиентов', lv: 'Klientu atsauksmes' },
    page_description: {
      ru: 'Наши клиенты делятся своим опытом с украшениями Pure Atelier',
      lv: 'Mūsu klienti dalās savā pieredzē ar Pure Atelier juvelierizstrādājumiem'
    }
  },
  gallery: {
    page_title: { ru: 'Коллекция', lv: 'Kolekcija' },
    page_description: {
      ru: 'Откройте для себя изысканные украшения ручной работы',
      lv: 'Atklājiet izsmalcinātus rokdarbu rotaslietas'
    }
  },
  order: {
    page_title: { ru: 'Заказ и Осмотр', lv: 'Pasūtījums un Apskate' },
    page_subtitle: { ru: 'Процесс покупки в Pure Atelier Riga', lv: 'Pirkšanas process Pure Atelier Riga' },
    engraving_title: { ru: 'Персональная Гравировка', lv: 'Personalizēta Gravēšana' },
    engraving_text: {
      ru: 'Сделайте ваше кольцо по-настоящему особенным! Pure Atelier предлагает услугу гравировки любых инициалов, имён или дат на ювелирном изделии.',
      lv: 'Padariet savu gredzenu patiesi īpašu! Pure Atelier piedāvā gravēšanas pakalpojumu ar jebkuriem iniciāļiem, vārdiem vai datumiem uz juvelierizstrādājuma.'
    },
    guarantee_title: { ru: 'Гарантия Pure Atelier', lv: 'Pure Atelier Garantija' },
    guarantee_items: {
      ru: 'Гарантия 12 месяцев на все изделия\nБесплатная профессиональная чистка раз в год\n30 дней на обмен\nСертификат подлинности для каждого бриллианта\nГарантия качества изготовления',
      lv: 'Garantija 12 mēneši visiem izstrādājumiem\nBezmaksas profesionāla tīrīšana reizi gadā\n30 dienas apmaiņai\nAutentiskuma sertifikāts katram dimantam\nIzgatavošanas kvalitātes garantija'
    },
    process_title: { ru: 'Процесс Покупки и Осмотра', lv: 'Pirkšanas un Apskates Process' },
    step1_title: { ru: '1. Свяжитесь с нами', lv: '1. Sazinieties ar mums' },
    step1_text: {
      ru: 'Позвоните по телефону +371 28 448 991, отправьте запрос на сайте или напишите в WhatsApp.',
      lv: 'Zvaniet pa tālruni +371 28 448 991, nosūtiet pieprasījumu vietnē vai rakstiet WhatsApp.'
    },
    step2_title: { ru: '2. Осмотр изделия', lv: '2. Izstrādājuma apskate' },
    step2_text: {
      ru: 'Встретьтесь с нашим экспертом, рассмотрите кольцо в деталях.',
      lv: 'Satiekat mūsu ekspertu, apskatiet gredzenu detalizēti.'
    },
    step3_title: { ru: '3. Оплата', lv: '3. Maksājums' },
    step3_text: {
      ru: 'Мы принимаем оплату наличными или банковским переводом.',
      lv: 'Mēs pieņemam maksājumu skaidrā naudā vai bankas pārskaitījumu.'
    },
    step4_title: { ru: '4. Доставка', lv: '4. Piegāde' },
    step4_text: {
      ru: 'Бесплатная доставка на осмотр по всей Риге. При покупке - бесплатная доставка по всей Европе!',
      lv: 'Bezmaksas piegāde apskatei visā Rīgā. Pērkot - bezmaksas piegāde visā Eiropā!'
    },
    cta_button: { ru: 'Записаться на осмотр', lv: 'Pierakstīties apskatei' }
  }
};

export const AdminContent = () => {
  const [pagesContent, setPagesContent] = useState(defaultContent);
  const [siteContent, setSiteContent] = useState({ contact_phone: '+371 28448991', contact_email: 'pure.atelier.riga@gmail.com' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState('header');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      // Загружаем контент страниц
      const response = await axios.get(`${API}/pages-content`);
      if (response.data && response.data.pages) {
        // Merge loaded content with defaults
        const merged = { ...defaultContent };
        Object.keys(response.data.pages).forEach(pageKey => {
          if (merged[pageKey]) {
            merged[pageKey] = { ...merged[pageKey], ...response.data.pages[pageKey] };
          } else {
            merged[pageKey] = response.data.pages[pageKey];
          }
        });
        setPagesContent(merged);
      }
      
      // Загружаем site-content (номер телефона и email)
      const siteResponse = await axios.get(`${API}/site-content`);
      if (siteResponse.data) {
        setSiteContent({
          contact_phone: siteResponse.data.contact_phone || '+371 28448991',
          contact_email: siteResponse.data.contact_email || 'pure.atelier.riga@gmail.com'
        });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Ошибка загрузки контента');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Сохраняем контент страниц
      await axios.put(`${API}/admin/pages-content`, {
        id: 'pages-content',
        pages: pagesContent
      }, {
        headers: authService.getAuthHeader()
      });
      
      // Сохраняем site-content (номер телефона и email)
      await axios.put(`${API}/admin/site-content`, {
        id: 'site-content',
        contact_phone: siteContent.contact_phone,
        contact_email: siteContent.contact_email
      }, {
        headers: authService.getAuthHeader()
      });
      
      toast.success('Контент сохранён!');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (page, field, lang, value) => {
    setPagesContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [field]: typeof prev[page]?.[field] === 'object' 
          ? { ...prev[page]?.[field], [lang]: value }
          : value
      }
    }));
  };

  const updateSimpleField = (page, field, value) => {
    setPagesContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [field]: value
      }
    }));
  };
  
  const updateSiteContentField = (field, value) => {
    setSiteContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Компонент для двуязычного текстового поля
  const TextField = ({ page, field, label, multiline = false, rows = 3 }) => {
    const value = pagesContent[page]?.[field] || { ru: '', lv: '' };
    
    return (
      <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
        <Label className="text-base font-medium">{label}</Label>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Русский (RU)</span>
            {multiline ? (
              <Textarea
                value={value.ru || ''}
                onChange={(e) => updateField(page, field, 'ru', e.target.value)}
                rows={rows}
                className="bg-background"
              />
            ) : (
              <Input
                value={value.ru || ''}
                onChange={(e) => updateField(page, field, 'ru', e.target.value)}
                className="bg-background"
              />
            )}
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Latviešu (LV)</span>
            {multiline ? (
              <Textarea
                value={value.lv || ''}
                onChange={(e) => updateField(page, field, 'lv', e.target.value)}
                rows={rows}
                className="bg-background"
              />
            ) : (
              <Input
                value={value.lv || ''}
                onChange={(e) => updateField(page, field, 'lv', e.target.value)}
                className="bg-background"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Простое поле (телефон, email)
  const SimpleField = ({ page, field, label, type = 'text' }) => {
    const value = pagesContent[page]?.[field] || '';
    
    return (
      <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
        <Label className="text-base font-medium">{label}</Label>
        <Input
          type={type}
          value={value}
          onChange={(e) => updateSimpleField(page, field, e.target.value)}
          className="bg-background max-w-md"
        />
      </div>
    );
  };

  // Рендер редактора для выбранной страницы
  const renderPageEditor = () => {
    switch (selectedPage) {
      case 'header':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Настройки хедера</CardTitle>
              <CardDescription>Редактирование контактной информации в шапке сайта</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
                <Label className="text-base font-medium">Номер телефона</Label>
                <Input
                  type="tel"
                  value={siteContent.contact_phone}
                  onChange={(e) => updateSiteContentField('contact_phone', e.target.value)}
                  className="bg-background max-w-md"
                  placeholder="+371 28448991"
                />
                <p className="text-sm text-muted-foreground">Этот номер отображается в шапке сайта на всех страницах</p>
              </div>
              
              <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
                <Label className="text-base font-medium">Email</Label>
                <Input
                  type="email"
                  value={siteContent.contact_email}
                  onChange={(e) => updateSiteContentField('contact_email', e.target.value)}
                  className="bg-background max-w-md"
                  placeholder="pure.atelier.riga@gmail.com"
                />
                <p className="text-sm text-muted-foreground">Email для контактов (отображается в футере и на странице контактов)</p>
              </div>
            </CardContent>
          </Card>
        );
        
      case 'home':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Hero секция</CardTitle>
                <CardDescription>Главный баннер на первом экране</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="home" field="hero_title" label="Заголовок" />
                <TextField page="home" field="hero_subtitle" label="Подзаголовок" />
                <TextField page="home" field="hero_description" label="Описание" multiline rows={3} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Секция отзывов</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="home" field="reviews_title" label="Заголовок" />
                <TextField page="home" field="reviews_description" label="Описание" multiline rows={2} />
              </CardContent>
            </Card>
          </div>
        );

      case 'about':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Заголовок страницы</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="about" field="page_title" label="Заголовок" />
                <TextField page="about" field="page_description" label="Описание" multiline rows={2} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Наша история</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="about" field="story_title" label="Заголовок секции" />
                <TextField page="about" field="story_text1" label="Параграф 1" multiline rows={3} />
                <TextField page="about" field="story_text2" label="Параграф 2" multiline rows={3} />
                <TextField page="about" field="story_text3" label="Параграф 3" multiline rows={3} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Преимущества</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="about" field="feature1_title" label="Блок 1 - Заголовок" />
                <TextField page="about" field="feature1_desc" label="Блок 1 - Описание" multiline rows={2} />
                <TextField page="about" field="feature2_title" label="Блок 2 - Заголовок" />
                <TextField page="about" field="feature2_desc" label="Блок 2 - Описание" multiline rows={2} />
                <TextField page="about" field="feature3_title" label="Блок 3 - Заголовок" />
                <TextField page="about" field="feature3_desc" label="Блок 3 - Описание" multiline rows={2} />
                <TextField page="about" field="feature4_title" label="Блок 4 - Заголовок" />
                <TextField page="about" field="feature4_desc" label="Блок 4 - Описание" multiline rows={2} />
              </CardContent>
            </Card>
          </div>
        );

      case 'contacts':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Заголовок страницы</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="contacts" field="page_title" label="Заголовок" />
                <TextField page="contacts" field="page_description" label="Описание" multiline rows={2} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Контактная информация</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <SimpleField page="contacts" field="phone" label="Телефон" type="tel" />
                <SimpleField page="contacts" field="email" label="Email" type="email" />
                <TextField page="contacts" field="location" label="Местоположение" />
                <TextField page="contacts" field="working_hours" label="Часы работы" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Форма связи</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="contacts" field="form_title" label="Заголовок" />
                <TextField page="contacts" field="form_description" label="Описание" multiline rows={3} />
              </CardContent>
            </Card>
          </div>
        );

      case 'reviews':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Страница отзывов</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TextField page="reviews" field="page_title" label="Заголовок" />
              <TextField page="reviews" field="page_description" label="Описание" multiline rows={2} />
            </CardContent>
          </Card>
        );

      case 'gallery':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Страница галереи</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TextField page="gallery" field="page_title" label="Заголовок" />
              <TextField page="gallery" field="page_description" label="Описание" multiline rows={2} />
            </CardContent>
          </Card>
        );

      case 'order':
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Заголовок страницы</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="order" field="page_title" label="Заголовок" />
                <TextField page="order" field="page_subtitle" label="Подзаголовок" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Процесс покупки</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="order" field="process_title" label="Заголовок секции" />
                <TextField page="order" field="step1_title" label="Шаг 1 - Заголовок" />
                <TextField page="order" field="step1_text" label="Шаг 1 - Текст" multiline rows={2} />
                <TextField page="order" field="step2_title" label="Шаг 2 - Заголовок" />
                <TextField page="order" field="step2_text" label="Шаг 2 - Текст" multiline rows={2} />
                <TextField page="order" field="step3_title" label="Шаг 3 - Заголовок" />
                <TextField page="order" field="step3_text" label="Шаг 3 - Текст" multiline rows={2} />
                <TextField page="order" field="step4_title" label="Шаг 4 - Заголовок" />
                <TextField page="order" field="step4_text" label="Шаг 4 - Текст" multiline rows={2} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Гравировка</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="order" field="engraving_title" label="Заголовок" />
                <TextField page="order" field="engraving_text" label="Текст" multiline rows={3} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Гарантия</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <TextField page="order" field="guarantee_title" label="Заголовок" />
                <TextField page="order" field="guarantee_items" label="Пункты гарантии (каждый с новой строки)" multiline rows={5} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Кнопка</CardTitle>
              </CardHeader>
              <CardContent>
                <TextField page="order" field="cta_button" label="Текст кнопки" />
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="loading">Загрузка...</div>;
  }

  return (
    <div data-testid="admin-content-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Type className="w-8 h-8 text-accent" />
            <div>
              <h1 className="font-heading text-4xl tracking-tight">Контент сайта</h1>
              <p className="text-muted-foreground">Редактируйте тексты на всех страницах</p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="uppercase tracking-widest"
            data-testid="save-content-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>

        {/* Выбор страницы */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Выберите страницу</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {PAGES.map((page) => {
                const Icon = page.icon;
                return (
                  <Button
                    key={page.id}
                    variant={selectedPage === page.id ? 'default' : 'outline'}
                    onClick={() => setSelectedPage(page.id)}
                    className="flex items-center gap-2"
                  >
                    <Icon className="w-4 h-4" />
                    {page.name}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Редактор выбранной страницы */}
        {renderPageEditor()}

        {/* Кнопка сохранения внизу */}
        <div className="flex justify-end mt-6 pt-6 border-t">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="uppercase tracking-widest"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Сохранение...' : 'Сохранить все изменения'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};
